import type {
  CreateRetentionDto,
  RetentionEntity,
  UpdateRetentionDto,
} from '@data-delivery/permission-ui-api';
import { retentionApi } from '@data-delivery/permission-ui-api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const retentionKeys = {
  all: ['retentions'] as const,
  list: () => [...retentionKeys.all, 'list'] as const,
  detail: (id: string) => [...retentionKeys.all, 'detail', id] as const,
};

export function useRetentionsQuery() {
  return useQuery<RetentionEntity[]>({
    queryKey: retentionKeys.list(),
    async queryFn() {
      const { data } = await retentionApi.getAllRetentions();
      return data ?? [];
    },
  });
}

export function useRetentionQuery(id: string | undefined) {
  return useQuery<RetentionEntity | undefined>({
    queryKey: id ? retentionKeys.detail(id) : [...retentionKeys.all, 'detail', 'none'],
    enabled: Boolean(id),
    async queryFn() {
      if (!id) {
        return undefined;
      }

      const { data } = await retentionApi.getRetentionById({ id });
      return data ?? undefined;
    },
  });
}

export function useCreateRetentionMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: [...retentionKeys.all, 'create'],
    async mutationFn(dto: CreateRetentionDto) {
      const { data } = await retentionApi.createRetention({ createRetentionDto: dto });
      return data;
    },
    onSuccess() {
      qc.invalidateQueries({ queryKey: retentionKeys.list() });
    },
  });
}

export function useUpdateRetentionMutation(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: [...retentionKeys.all, 'update', id],
    async mutationFn(dto: Partial<UpdateRetentionDto> & { lastModified: number }) {
      const base: UpdateRetentionDto = { lastModified: dto.lastModified };
      const payload: UpdateRetentionDto = { ...base };
      if (dto.name !== undefined) {
        payload.name = dto.name;
      }

      if (dto.path !== undefined) {
        payload.path = dto.path;
      }

      if (dto.enabled !== undefined) {
        payload.enabled = dto.enabled;
      }

      if (dto.ignoreGlobalChecks !== undefined) {
        payload.ignoreGlobalChecks = dto.ignoreGlobalChecks;
      }

      if (dto.service !== undefined) {
        payload.service = dto.service;
      }

      if (dto.type !== undefined) {
        payload.type = dto.type;
      }

      if (dto.ageAccessed !== undefined) {
        payload.ageAccessed = dto.ageAccessed;
      }

      if (dto.ageModified !== undefined) {
        payload.ageModified = dto.ageModified;
      }

      if (dto.filterType !== undefined) {
        payload.filterType = dto.filterType;
      }

      if (dto.product !== undefined) {
        payload.product = dto.product;
      }

      const { data } = await retentionApi.updateRetention({ id, updateRetentionDto: payload });
      return data;
    },
    onSuccess() {
      qc.invalidateQueries({ queryKey: retentionKeys.detail(id) });
      qc.invalidateQueries({ queryKey: retentionKeys.list() });
    },
  });
}

export function useDeleteRetentionMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: [...retentionKeys.all, 'delete'],
    async mutationFn(id: string) {
      const { data } = await retentionApi.deleteRetention({ id });
      return data;
    },
    onSuccess() {
      qc.invalidateQueries({ queryKey: retentionKeys.list() });
    },
  });
}
